import React, { useState, useEffect, useCallback } from "react";
import InlineSVG from "react-inlinesvg";
import Auth from "../Auth";
import { cn } from "src/lib/cn";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Text } from "src/sudokit/components/Text";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { Input } from "src/sudokit/components/Input";
import { Spinner } from "src/sudokit/components/Spinner";

interface FeedbackItem {
  id: string;
  chatId: string;
  messageId: string;
  userId: string;
  projectId: string;
  rating: number;
  feedbackText: string | null;
  modelKey: string | null;
  mode: string | null;
  createdAt: string;
  messagePreview: string | null;
}

type SortField = "createdAt" | "rating" | "modelKey" | "mode" | "userId";
type SortDirection = "asc" | "desc";

const LIMIT = 50;

export default function AgenticChatFeedback() {
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, limit: LIMIT };
      if (ratingFilter !== null) params.rating = ratingFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const result = await Auth.fetchWithIdToken("/api/admin/agentic-chat-feedback/list", params);
      if (result.success) {
        setFeedback(result.feedback);
        setTotal(result.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching feedback:", error);
    } finally {
      setLoading(false);
    }
  }, [page, ratingFilter, startDate, endDate]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const handleExport = async (format: "csv" | "json") => {
    try {
      const params: Record<string, unknown> = { format };
      if (ratingFilter !== null) params.rating = ratingFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const result = await Auth.fetchWithIdToken("/api/admin/agentic-chat-feedback/export", params);

      const blob = format === "csv"
        ? new Blob([result], { type: "text/csv" })
        : new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agentic-chat-feedback.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedFeedback = [...feedback].sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;
    switch (sortField) {
      case "createdAt":
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
        break;
      case "rating":
        aVal = a.rating;
        bVal = b.rating;
        break;
      case "modelKey":
        aVal = (a.modelKey || "").toLowerCase();
        bVal = (b.modelKey || "").toLowerCase();
        break;
      case "mode":
        aVal = (a.mode || "").toLowerCase();
        bVal = (b.mode || "").toLowerCase();
        break;
      case "userId":
        aVal = a.userId.toLowerCase();
        bVal = b.userId.toLowerCase();
        break;
      default:
        return 0;
    }
    if (aVal > bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal < bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(total / LIMIT);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <InlineSVG
          src="/assets/down-arrow.svg"
          className="w-2 h-2 opacity-0 group-hover:opacity-30 transition-opacity [&_path]:fill-app-grey-2"
        />
      );
    }
    return (
      <InlineSVG
        src="/assets/down-arrow.svg"
        className={cn(
          "w-2 h-2 transition-transform [&_path]:fill-app-primary-cta-high-contrast",
          sortDirection === "desc" && "rotate-180",
        )}
      />
    );
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-6">
      <div className="sm:flex sm:items-center mb-8">
        <div className="sm:flex-auto gap-1 flex-col flex">
          <Text className="text-3xl font-semibold bg-gradient-to-r from-app-grey-0 to-app-grey-1 bg-clip-text text-transparent">
            Agentic Chat Feedback
          </Text>
          <Text className="text-base text-app-grey-1">
            Review and export user feedback on agentic chat responses
          </Text>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <MenuSelect
            label="Filter by rating"
            value={ratingFilter === null ? "all" : String(ratingFilter)}
            onChange={(value: string) => {
              setRatingFilter(value === "all" ? null : Number(value));
              setPage(1);
            }}
            options={[
              { value: "all", label: "All Ratings" },
              { value: "1", label: "Likes" },
              { value: "-1", label: "Dislikes" },
              { value: "0", label: "Retracted" },
            ]}
            placeholder="Filter by rating"
          />

          <div className="flex items-center gap-2">
            <Text className="text-sm text-app-grey-1">From</Text>
            <Input
              type="date"
              mode="default"
              value={startDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setStartDate(e.target.value); setPage(1); }}
              className="w-36 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <Text className="text-sm text-app-grey-1">To</Text>
            <Input
              type="date"
              mode="default"
              value={endDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setEndDate(e.target.value); setPage(1); }}
              className="w-36 text-sm"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <SudoButton variant="secondaryGrey" size="small" onClick={() => handleExport("csv")}>
            Export CSV
          </SudoButton>
          <SudoButton variant="secondaryGrey" size="small" onClick={() => handleExport("json")}>
            Export JSON
          </SudoButton>
        </div>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="text-center">
              <Spinner size="lg" className="mx-auto mb-4" />
              <Text className="text-sm font-medium text-app-grey-1">Loading feedback...</Text>
            </div>
          </div>
        ) : feedback.length === 0 ? (
          <div className="text-center py-12 px-4 bg-app-menu-color rounded-xl border border-app-menu-color-hover shadow-card animate-in fade-in">
            <Text className="text-app-grey-1">No feedback found matching the current filters.</Text>
          </div>
        ) : (
          <>
            <div className="rounded-xl bg-app-menu-color border border-app-menu-color-hover shadow-card overflow-auto scrollbar-track-transparent scrollbar-corner-transparent scrollbar-thumb-slate-400">
              <table className="min-w-full divide-y divide-app-menu-color-hover">
                <thead className="bg-app-menu-color h-9">
                  <tr className="border-b border-app-menu-color-hover">
                    <th
                      scope="col"
                      className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
                      onClick={() => handleSort("createdAt")}
                    >
                      <div className="flex items-center gap-2">
                        <span>Date</span>
                        <SortIcon field="createdAt" />
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
                      onClick={() => handleSort("rating")}
                    >
                      <div className="flex items-center gap-2">
                        <span>Rating</span>
                        <SortIcon field="rating" />
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider"
                    >
                      Feedback
                    </th>
                    <th
                      scope="col"
                      className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
                      onClick={() => handleSort("userId")}
                    >
                      <div className="flex items-center gap-2">
                        <span>User ID</span>
                        <SortIcon field="userId" />
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
                      onClick={() => handleSort("modelKey")}
                    >
                      <div className="flex items-center gap-2">
                        <span>Model</span>
                        <SortIcon field="modelKey" />
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="group px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider cursor-pointer hover:text-app-grey-0 transition-colors"
                      onClick={() => handleSort("mode")}
                    >
                      <div className="flex items-center gap-2">
                        <span>Mode</span>
                        <SortIcon field="mode" />
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider"
                    >
                      Message Preview
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFeedback.map((f, index) => (
                    <tr
                      key={f.id}
                      className="hover:bg-app-menu-color-hover transition-colors animate-in fade-in slide-in-from-bottom-2 duration-300"
                      style={{ animationDelay: `${Math.min(index * 30, 500)}ms`, animationFillMode: "both" }}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0">
                        {new Date(f.createdAt).toLocaleDateString()}{" "}
                        {new Date(f.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold",
                            f.rating === 1 && "bg-green-500/10 text-green-500",
                            f.rating === -1 && "bg-red-500/10 text-red-500",
                            f.rating === 0 && "bg-app-grey-3/30 text-app-grey-1",
                          )}
                        >
                          <span
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              f.rating === 1 && "bg-green-500",
                              f.rating === -1 && "bg-red-500",
                              f.rating === 0 && "bg-app-grey-2",
                            )}
                          />
                          {f.rating === 1 ? "Like" : f.rating === -1 ? "Dislike" : "Retracted"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm max-w-[300px]">
                        {f.feedbackText ? (
                          <span className="text-app-grey-0 break-words line-clamp-3">{f.feedbackText}</span>
                        ) : (
                          <span className="text-app-grey-2 italic">No text</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <a
                          href={`/admin/user/${f.userId}`}
                          className="text-xs font-mono text-app-primary-cta-high-contrast hover:brightness-110 transition-all"
                        >
                          {f.userId.slice(0, 12)}...
                        </a>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0">
                        {f.modelKey || <span className="text-app-grey-2">-</span>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {f.mode ? (
                          <span
                            className={cn(
                              "inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold",
                              f.mode === "write" ? "bg-blue-500/10 text-blue-500" : "bg-purple-500/10 text-purple-500",
                            )}
                          >
                            {f.mode.charAt(0).toUpperCase() + f.mode.slice(1)}
                          </span>
                        ) : (
                          <span className="text-sm text-app-grey-2">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 max-w-[250px]">
                        <span
                          className="text-sm text-app-grey-1 block truncate"
                          title={f.messagePreview || ""}
                        >
                          {f.messagePreview || "-"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4 pb-6">
              <Text className="text-sm text-app-grey-1">
                Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total}
              </Text>
              <div className="flex items-center gap-2">
                <SudoButton
                  variant="secondaryGrey"
                  size="small"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </SudoButton>
                <Text className="text-sm text-app-grey-0 px-2">
                  Page {page} of {totalPages}
                </Text>
                <SudoButton
                  variant="secondaryGrey"
                  size="small"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </SudoButton>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
